import React, { Fragment } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useFormikContext } from "formik";
import { STATE_KEYS } from "utils/constants/state";
import get from "lodash/get";
import DraggableProductCollectionItem from "./DraggableProductCollectionItem";
import { listDragProps, LIST_DESTINATIONS } from "utils/drag";
import Padder from "components/Padder";
import {
  addProductToCollection,
  reorderProductCollection
} from "utils/product";
import Analytics, { DRAG_PRODUCT, SHOW_PRODUCT } from "utils/analytics";
import MenuNavHeader from "components/MenuNavHeader";
import { BUTTON_TAGS, secondaryBackButton, UI_THEME } from "utils/constants/ui";
import CTA from "components/CTA";
import { getFieldPath } from "utils/form";
import { getSchemaValuesForEditor } from "utils/schema";
import { usePageBuilderContext } from "utils/context";
import DebounceTextarea from "components/DebounceTextarea";

/**
 * @param {Object} props
 * @param {String} props.name - manifest.data.content.1b46fe52-0377-419c-9d49-791b728c3296.data.products
 * @returns
 */
const ProductOrderSettings = (props) => {
  const name = props.name;
  const schema = props.schema;
  const builderCtx = usePageBuilderContext();
  const tracking = builderCtx ? builderCtx.tracking : {};

  const propertyKey = "products";
  const { rootPath, template } = getSchemaValuesForEditor({
    name,
    propertyKey,
    schema
  });

  const {
    values,
    touched,
    setValues,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const titlePath = getFieldPath(rootPath, "title");
  const subtitlePath = getFieldPath(rootPath, "subtitle");
  const actionCopyPath = getFieldPath(rootPath, "actions[0].copy");
  const productsPath = getFieldPath(rootPath, propertyKey);

  const title = get(values, titlePath);
  const subtitle = get(values, subtitlePath);
  const actionCopy = get(values, actionCopyPath);
  const products = get(values, productsPath);
  const hasProducts = products.length > 0;

  const goBack = () => {
    setFieldValue(STATE_KEYS.EDITOR.MENU_INTENT, null);
    setFieldValue(STATE_KEYS.EDITOR.MENU_ACTIVE_INDEX, null);
  };

  const buttonProps = secondaryBackButton({ onClick: () => goBack() });

  const productsOnTable = products.map((product, productIndex) => {
    const uuid = product.uuid;

    const itemProps = {
      name,
      uid: uuid,
      uuid: uuid,
      title: product.title,
      images: product.images,
      index: productIndex,
      canRemove: true,
      canAdd: false,
      dragDisabled: false,
      onClick: () => {
        Analytics.track(SHOW_PRODUCT, {
          ...tracking,
          product: uuid
        });
        setFieldValue(STATE_KEYS.EDITOR.MENU_ACTIVE_INDEX, productIndex);
      }
    };
    return <DraggableProductCollectionItem key={uuid} {...itemProps} />;
  });

  return (
    <Fragment>
      <MenuNavHeader navSetters={[buttonProps]} label="Products" />
      <div className="w-100 pa3">
        <label className="f6 mb2 dib fw6">Title</label>
        <DebounceTextarea
          inputValue={title}
          placeholder="Collection title"
          debounce={500}
          minRows={1}
          name={titlePath}
          valueSet={(val) => {
            setFieldValue(titlePath, val);
            if (!get(touched, titlePath)) {
              setFieldTouched(titlePath, true);
            }
          }}
        />
        <div className="w-100 pt3">
          <label className="f6 mb2 dib fw6">Subtitle</label>
          <DebounceTextarea
            inputValue={subtitle}
            placeholder="Collection subtitle"
            debounce={500}
            minRows={1}
            name={subtitlePath}
            valueSet={(val) => {
              setFieldValue(subtitlePath, val);
              if (!get(touched, subtitlePath)) {
                setFieldTouched(subtitlePath, true);
              }
            }}
          />
        </div>
        <div className="w-100 pt3">
          <label className="f6 mb2 dib fw6">Button</label>
          <DebounceTextarea
            inputValue={actionCopy}
            placeholder="Button text"
            debounce={500}
            minRows={1}
            name={actionCopyPath}
            valueSet={(val) => {
              setFieldValue(actionCopyPath, val);
              if (!get(touched, actionCopyPath)) {
                setFieldTouched(actionCopyPath, true);
              }
            }}
          />
        </div>
      </div>
      <div className="flex flex-row justify-between items-center ph3 pv3">
        <div className="fw6 f6">Products</div>
        {hasProducts && props.addProduct && (
          <div
            className="f6 fw6 link__decoration--hover color-primary pointer"
            onClick={props.addProduct}
          >
            Add
          </div>
        )}
      </div>
      {hasProducts ? (
        <DragDropContext
          onDragEnd={reorderProductCollection({
            setFieldValue,
            setFieldTouched,
            products,
            productsPath,
            tracking: {
              event: DRAG_PRODUCT,
              data: {
                ...tracking
              }
            }
          })}
        >
          <Droppable droppableId={LIST_DESTINATIONS.PRODUCT_COLLECTION}>
            {(provided, snapshot) => (
              <ul {...listDragProps({ provided, snapshot })}>
                {productsOnTable}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div className="ph3">
          <CTA
            customClasses={{
              container: "hairline-1 ba br2 pt3 pb3",
              header: "f5 ph3 fw6",
              subheader: "f6 ph3 lh-copy",
              actionsWrapper: "flex flex-row z-1 pr3 justify-between"
            }}
            subheader="Add a product."
            actions={[
              {
                copy: "Add",
                tag: BUTTON_TAGS.BUTTON,
                theme: UI_THEME.SLIM,
                onClick: addProductToCollection({
                  values,
                  setValues,
                  setFieldTouched,
                  template,
                  productsPath
                })
              }
            ]}
          />
        </div>
      )}
      <Padder theme="visible" />
    </Fragment>
  );
};

export default ProductOrderSettings;
